// Money lock — hides rand values on site tenants behind a per-tenant PIN.
// DB side: mig 20260810164741 (tenant_money_lock + verify_money_lock_pin).
//
// WHAT THIS IS. On MFP the data capturers key hours and units in while signed
// in as the ADMIN's profile. There is no second identity, so RLS and custom
// roles cannot help — the session genuinely IS the admin. This is a SCREEN
// LOCK: money hidden by default, revealed for a short window by whoever knows
// the PIN.
//
// WHAT THIS IS NOT. Not an access boundary. The rows still reach the browser
// and the session still holds admin rights, so devtools defeats it — including
// by editing the sessionStorage key below. That is inherent to a shared login
// and is not a bug to be fixed here; the fix is separate logins. Treat this as
// the lock on a filing cabinet in a shared office, not as encryption.
//
// The PIN itself IS verified server-side (verify_money_lock_pin), so the lock
// cannot be picked by reading the JS bundle — the weak link is the session, not
// the secret.
//
// State lives in sessionStorage, deliberately: closing the browser re-locks.
// A module-level store rather than a context provider because ~150 render sites
// read it, and every one of them should re-render the instant it flips.

import { useCallback, useSyncExternalStore } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withTimeout } from '@/lib/saveTimeout'
import { useActiveTenant, type TenantKind } from '@/lib/useActiveTenant'

// Kinds the lock applies to. The PAYROLL tenant is where money is SUPPOSED to
// be looked at, so it is never locked — that is the whole point of moving
// authoring there. Back-office kinds (EXPORTER, BUYER…) aren't shared-login
// capture surfaces and are left alone.
const LOCKABLE_KINDS: readonly TenantKind[] = ['FARM', 'PACKHOUSE', 'HARVEST']

const KEY = (tenantId: string) => `kf49.moneyLock.until.${tenantId}`

// ---------------------------------------------------------------------------
// Store — unlock expiry per tenant, mirrored to sessionStorage
// ---------------------------------------------------------------------------
const listeners = new Set<() => void>()
let timer: ReturnType<typeof setTimeout> | null = null

function emit(): void { listeners.forEach(l => l()) }

function readUntil(tenantId: string | null): number {
  if (!tenantId) return 0
  try {
    const raw = sessionStorage.getItem(KEY(tenantId))
    const n = raw ? Number(raw) : 0
    return Number.isFinite(n) ? n : 0
  } catch { return 0 }
}

function writeUntil(tenantId: string, until: number): void {
  try {
    if (until > Date.now()) sessionStorage.setItem(KEY(tenantId), String(until))
    else sessionStorage.removeItem(KEY(tenantId))
  } catch { /* private mode / quota — the lock just won't persist the unlock */ }
  // Re-lock exactly when the window closes, so a screen left open goes dark on
  // its own rather than staying unlocked until the next render happens to run.
  if (timer) clearTimeout(timer)
  const ms = until - Date.now()
  if (ms > 0) timer = setTimeout(emit, ms + 50)
  emit()
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

// ---------------------------------------------------------------------------
// Synchronous read, for module-level formatters
// ---------------------------------------------------------------------------
// Roughly a third of the app's money helpers are declared at MODULE level —
// often because they're used by module-level row-render functions in print
// sheets. Those can't call a hook, and converting each of their call sites to
// <Money/> is a large, error-prone edit for no behavioural gain.
//
// So the lock state is mirrored to a plain variable that `fmtMoneyAuto()` can
// read synchronously. The re-render that makes it visible comes from MoneyGate,
// which calls useMoneyLock() inside RequireModule — i.e. on EVERY module route —
// so a lock flip re-renders each page's tree and these helpers run again.
//
// Caveat worth knowing: a `React.memo`'d subtree that takes no changing props
// could hold a stale value until its next render. That's why the surfaces that
// matter most (payroll, payslips, advances) are gated at the route/tab level
// rather than relying on this.
let lockedNow = false

/** Is money hidden right now? Non-reactive — see the note above. */
export function isMoneyLockedNow(): boolean { return lockedNow }

// ---------------------------------------------------------------------------
// PIN validation — pure, so it can be tested without a DOM
// ---------------------------------------------------------------------------
/**
 * Check what an admin typed into the settings card, mirroring the checks
 * `set_money_lock_pin()` enforces server-side (mig 20260810164741). Returns an
 * operator-readable message, or null when the input is good.
 *
 * Extracted from the card's `save()` so the rules are testable and stated once:
 * a form that disagrees with the RPC either blocks a PIN the server would take,
 * or invites one it will reject.
 */
export function pinInputProblem(pin: string, confirm: string, minutes: string): string | null {
  if (pin.trim().length < 4) return 'PIN must be at least 4 characters.'
  if (pin !== confirm)       return 'The two PINs don’t match.'
  const m = Number(minutes)
  if (!Number.isInteger(m) || m < 1 || m > 240) {
    return 'Unlock window must be between 1 and 240 minutes.'
  }
  return null
}

export interface MoneyLockState {
  /** The tenant has switched the feature on. */
  enabled:         boolean
  /** The lock state has been read (false while loading). */
  known:           boolean
  /** Money should be hidden right now. */
  locked:          boolean
  /** Epoch ms the current unlock expires; 0 when locked. */
  expiresAt:       number
  /** Too many wrong PINs — don't invite another until this passes. */
  lockedOutUntil:  Date | null
  unlockMinutes:   number
  /** Verify a PIN server-side; on success, unlock for `unlockMinutes`. */
  unlock:          (pin: string) => Promise<boolean>
  /** Re-lock immediately (the "done looking" button). */
  lock:            () => void
}

interface StateRow { enabled: boolean; unlock_minutes: number; locked_out_until: string | null }

// The tenant's lock config. Split out of useMoneyLock so the composition below
// reads as "config + unlock window + two actions" rather than carrying the
// query's offline handling inline as well.
function useMoneyLockConfig(tenantId: string | null, applies: boolean) {
  const qc = useQueryClient()
  return useQuery<StateRow | null>({
    queryKey: ['money-lock-state', tenantId],
    enabled:  applies && !!tenantId,
    staleTime: 60_000,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('money_lock_state').maybeSingle()
      if (error) {
        // Offline / unreachable. Fail toward LESS exposure: keep whatever we
        // last knew for this tenant. A tenant that never enabled the lock has
        // no cached row, so the feature stays off rather than blacking out
        // money for everyone the first time the network hiccups.
        return qc.getQueryData<StateRow | null>(['money-lock-state', tenantId]) ?? null
      }
      return (data ?? null) as StateRow | null
    },
  })
}

// The unlock action, lifted out of the hook so useMoneyLock reads as a
// composition rather than also carrying the RPC's error handling.
async function verifyAndUnlock(
  pin: string, tenantId: string | null, unlockMin: number, qc: QueryClient,
): Promise<boolean> {
  if (!tenantId) return false
  const { data: ok, error } = await withTimeout(
    supabase.rpc('verify_money_lock_pin', { p_pin: pin }), 'moneyLock: verify_money_lock_pin')
  // Refresh either way, so the UI can start saying "locked out until …" as soon
  // as the server decides that's where we are.
  void qc.invalidateQueries({ queryKey: ['money-lock-state', tenantId] })
  if (error || ok !== true) return false
  writeUntil(tenantId, Date.now() + unlockMin * 60_000)
  return true
}

// Composition hook: tenant kind + config query + unlock window + two actions.
// The two pieces worth having on their own are already out (useMoneyLockConfig,
// verifyAndUnlock) and neither moved the score, which says it is driven by the
// hook count rather than by branching — splitting further would fragment one
// coherent state machine to please a metric.
// fallow-ignore-next-line complexity
export function useMoneyLock(): MoneyLockState {
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  const kind     = tenant?.kind ?? null
  const qc       = useQueryClient()

  const applies = !!kind && LOCKABLE_KINDS.includes(kind)

  const { data } = useMoneyLockConfig(tenantId, applies)

  const enabled = applies && (data?.enabled ?? false)

  const until = useSyncExternalStore(
    subscribe,
    () => readUntil(tenantId),
    () => 0,          // SSR/first paint: locked. Never flash money then hide it.
  )
  const unlocked  = until > Date.now()
  const unlockMin = data?.unlock_minutes ?? 15

  const unlock = useCallback(
    (pin: string) => verifyAndUnlock(pin, tenantId, unlockMin, qc),
    [tenantId, unlockMin, qc])

  const lock = useCallback(() => {
    if (tenantId) writeUntil(tenantId, 0)
  }, [tenantId])

  // Mirror for the synchronous readers above. Assigning during render is safe
  // here: it's a plain idempotent write derived entirely from this render's own
  // values, with no subscriber notified.
  lockedNow = enabled && !unlocked

  return {
    enabled,
    known:          data !== undefined,
    locked:         enabled && !unlocked,
    expiresAt:      unlocked ? until : 0,
    lockedOutUntil: data?.locked_out_until ? new Date(data.locked_out_until) : null,
    unlockMinutes:  unlockMin,
    unlock,
    lock,
  }
}
